import React, { useState, useEffect, useRef } from 'react';
import './LegalNav.css';

/**
 * Helper function for smooth scrolling to an element with a custom offset
 */
export const scrollToSection = (targetId, isMobile = false) => {
  const elem = document.getElementById(targetId);
  if (elem) {
    const headerOffset = isMobile ? 75 : 30;
    const elementPosition = elem.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth'
    });

    // Subtle highlight pulse on target heading for user feedback
    const heading = elem.querySelector('h3') || elem;
    if (heading) {
      heading.style.transition = 'background-color 0.4s ease, border-radius 0.4s ease';
      heading.style.backgroundColor = 'rgba(43, 92, 53, 0.12)';
      heading.style.borderRadius = '6px';
      setTimeout(() => {
        heading.style.backgroundColor = 'transparent';
      }, 1400);
    }
  }
};

/**
 * Custom hook to detect the currently active section in the viewport
 */
export const useActiveSection = (sections) => {
  const [activeId, setActiveId] = useState(sections?.[0]?.id || '');

  useEffect(() => {
    if (!sections || sections.length === 0) return;

    const observerCallback = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setActiveId(entry.target.id);
        }
      });
    };

    const observerOptions = {
      root: null,
      rootMargin: '-80px 0px -65% 0px',
      threshold: 0
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);

    sections.forEach((sec) => {
      const el = document.getElementById(sec.id);
      if (el) observer.observe(el);
    });

    return () => {
      sections.forEach((sec) => {
        const el = document.getElementById(sec.id);
        if (el) observer.unobserve(el);
      });
    };
  }, [sections]);

  return [activeId, setActiveId];
};

/**
 * Mobile Collapsible "Jump to Section" Dropdown
 */
export const LegalMobileDropdown = ({ sections, activeSection, onSelectSection }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleOutsideClick);
      document.addEventListener('touchstart', handleOutsideClick);
    }

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('touchstart', handleOutsideClick);
    };
  }, [isOpen]);

  const activeObj = sections.find((s) => s.id === activeSection);

  const handleItemClick = (e, targetId) => {
    e.preventDefault();
    setIsOpen(false);
    if (onSelectSection) {
      onSelectSection(targetId);
    } else {
      scrollToSection(targetId, true);
    }
  };

  return (
    <div className="legal-mobile-nav-wrapper d-block d-md-none" ref={dropdownRef}>
      <div className={`legal-mobile-nav-card ${isOpen ? 'open' : ''}`}>
        <button
          type="button"
          className="legal-mobile-nav-trigger"
          onClick={() => setIsOpen((prev) => !prev)}
          aria-expanded={isOpen}
          aria-label="Toggle Section Navigation"
        >
          <div className="legal-mobile-nav-trigger-left">
            <span className="legal-mobile-nav-icon" aria-hidden="true">
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="16" y1="13" x2="8" y2="13" />
                <line x1="16" y1="17" x2="8" y2="17" />
                <line x1="10" y1="9" x2="8" y2="9" />
              </svg>
            </span>
            <span className="legal-mobile-nav-title">Jump to Section</span>
            {activeObj && (
              <span className="legal-mobile-nav-current" title={activeObj.title}>
                {activeObj.title}
              </span>
            )}
          </div>
          <div className={`legal-mobile-nav-chevron ${isOpen ? 'rotated' : ''}`}>
            <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
              <path
                d="M5 7.5L10 12.5L15 7.5"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        </button>

        {isOpen && (
          <div className="legal-mobile-nav-menu">
            {sections.map((sec) => {
              const isActive = sec.id === activeSection;
              return (
                <button
                  key={sec.id}
                  type="button"
                  className={`legal-mobile-nav-item ${isActive ? 'active' : ''}`}
                  onClick={(e) => handleItemClick(e, sec.id)}
                >
                  <span className="legal-mobile-nav-item-title">{sec.title}</span>
                  {isActive && <span className="legal-mobile-nav-item-dot" />}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Desktop & Tablet Left Sidebar
 */
export const LegalDesktopSidebar = ({ sections, activeSection, onSelectSection }) => {
  const handleItemClick = (e, targetId) => {
    e.preventDefault();
    if (onSelectSection) {
      onSelectSection(targetId);
    } else {
      scrollToSection(targetId, false);
    }
  };

  return (
    <div className="sticky-top" style={{ top: '100px', zIndex: 10 }}>
      <div className="legal-desktop-sidebar-card">
        <h5 className="poly fw-bold mb-3 color-primary">Sections</h5>
        <ul className="list-unstyled mb-0" style={{ fontSize: '14px' }}>
          {sections.map((sec) => {
            const isActive = sec.id === activeSection;
            return (
              <li key={sec.id} className="mb-2">
                <a
                  href={`#${sec.id}`}
                  className={`legal-desktop-sidebar-link ${isActive ? 'active' : ''}`}
                  onClick={(e) => handleItemClick(e, sec.id)}
                >
                  {sec.title}
                </a>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};

const LegalNav = {
  MobileDropdown: LegalMobileDropdown,
  DesktopSidebar: LegalDesktopSidebar,
  scrollToSection,
  useActiveSection
};

export default LegalNav;
